<script setup>
import { computed } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  log: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(['close']);

const formattedRequest = computed(() => {
  if (!props.log?.request_payload) return 'Tidak ada payload request';
  try {
    const parsed = typeof props.log.request_payload === 'string'
      ? JSON.parse(props.log.request_payload)
      : props.log.request_payload;
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return String(props.log.request_payload);
  }
});

const formattedResponse = computed(() => {
  if (!props.log?.response_payload) return 'Tidak ada payload response';
  try {
    const parsed = typeof props.log.response_payload === 'string'
      ? JSON.parse(props.log.response_payload)
      : props.log.response_payload;
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return String(props.log.response_payload);
  }
});

const isSuccess = computed(() => {
  const code = props.log?.status_code;
  return code >= 200 && code < 300;
});
</script>

<template>
  <woot-modal :show="show" :on-close="() => emit('close')">
    <woot-modal-header
      header-title="Detail Log Panggilan API"
      :header-content="log ? `${log.service_name} • ${log.created_at}` : ''"
    />

    <div v-if="log" class="p-6 flex flex-col gap-4 text-xs">
      <!-- Info Meta Ringkas -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-n-alpha-1 p-3 rounded-xl border border-n-weak">
        <div>
          <span class="text-n-slate-11 block font-medium">Method & Endpoint</span>
          <span class="font-mono font-bold text-n-slate-12">{{ log.http_method }} {{ log.endpoint }}</span>
        </div>
        <div>
          <span class="text-n-slate-11 block font-medium">Status Code</span>
          <span
            class="font-bold px-2 py-0.5 rounded-full inline-block mt-0.5"
            :class="isSuccess ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-ruby-100 text-ruby-800 dark:bg-ruby-950 dark:text-ruby-300'"
          >
            {{ log.status_code }} {{ isSuccess ? 'OK' : 'Error' }}
          </span>
        </div>
        <div>
          <span class="text-n-slate-11 block font-medium">Waktu Respon (Latency)</span>
          <span class="font-bold text-n-slate-12">{{ log.duration_ms }} ms</span>
        </div>
        <div>
          <span class="text-n-slate-11 block font-medium">Modul Layanan</span>
          <span class="font-semibold text-w-brand-primary">{{ log.service_name }}</span>
        </div>
      </div>

      <!-- Error Message jika ada -->
      <div v-if="log.error_message" class="bg-ruby-50 border border-ruby-200 text-ruby-900 dark:bg-ruby-950/40 dark:border-ruby-800 dark:text-ruby-300 p-3 rounded-xl">
        <span class="font-bold block mb-1">Pesan Kesalahan:</span>
        <p class="font-mono text-xs">{{ log.error_message }}</p>
      </div>

      <!-- Request Payload -->
      <div class="flex flex-col gap-1.5">
        <span class="font-bold text-n-slate-12 flex items-center gap-1.5">
          <span class="i-lucide-arrow-up-right text-blue-600"></span> Request Payload (Data Dikirim AI)
        </span>
        <pre class="bg-slate-900 text-emerald-400 p-3.5 rounded-xl font-mono overflow-x-auto text-[11px] max-h-48 shadow-inner">{{ formattedRequest }}</pre>
      </div>

      <!-- Response Payload -->
      <div class="flex flex-col gap-1.5">
        <span class="font-bold text-n-slate-12 flex items-center gap-1.5">
          <span class="i-lucide-arrow-down-left text-emerald-600"></span> Response Payload (Balasan API Cetakers)
        </span>
        <pre class="bg-slate-900 text-sky-300 p-3.5 rounded-xl font-mono overflow-x-auto text-[11px] max-h-60 shadow-inner">{{ formattedResponse }}</pre>
      </div>

      <div class="flex justify-end pt-2">
        <button
          type="button"
          class="px-4 py-2 text-xs font-semibold bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-12 rounded-lg transition-colors"
          @click="emit('close')"
        >
          Tutup Detail
        </button>
      </div>
    </div>
  </woot-modal>
</template>
